import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import useCrawlerNotFound from '../seo/useCrawlerNotFound';

/**
 * Catch-all page for URLs that don't exist.
 *
 * Before this (2026-10-05) an unknown URL rendered an empty app shell with
 * HTTP 200, so crawlers saw endless "pages" that were really copies of the
 * homepage ("soft 404s"). The SPA host can only ever return 200, so
 * useCrawlerNotFound marks the page noindex, which is what stops crawlers
 * indexing it. It also sets a prerender-status-code tag, which produced a
 * real HTTP 404 while Prerender.io sat in front of the site; that service was
 * removed on 2026-10-15, so an unknown URL now answers 200 with a noindex
 * page until a replacement for the status code exists.
 */
export default function NotFoundPage() {
  useCrawlerNotFound(true);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Page not found | Ariv';
    return () => {
      document.title = prevTitle;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <p className="text-sm font-semibold text-[#1F3A68] uppercase tracking-widest">404</p>
        <h1 className="mt-2 text-3xl font-bold text-gray-900">We can't find that page</h1>
        <p className="mt-3 text-gray-600">
          The link may be out of date, or the page may have moved.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link to="/" className="px-4 py-2 rounded-lg bg-[#1F3A68] text-white font-semibold">
            Go to the homepage
          </Link>
          <Link to="/become-host" className="px-4 py-2 rounded-lg border border-[#1F3A68] text-[#1F3A68] font-semibold">
            Become a host
          </Link>
          <Link to="/help" className="px-4 py-2 rounded-lg text-[#1F3A68] font-semibold">
            Help Centre
          </Link>
        </div>
      </div>
    </div>
  );
}
